import { useEffect } from "react";
import { Modal, Form, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";

import { authSelector } from "../reducers/index.js";
import { Button } from "./Button.tsx";
import Switch from "./Switch.jsx";
import { upsertMfaSettings } from "@hr/api/endpoints/organization-data-rest-controller/organization-data-rest-controller.ts";
import { getGetUserMfaStatusQueryKey, useGetUserMfaStatus, verifyCodeAndEnableMFA, verifyMFAeMail } from "@hr/api/endpoints/index.ts";

// TODO: save locale
export function MfaSetup({ hideModal }) {
  const { t } = useTranslation();
  const { user_name } = useSelector(authSelector);

  const { data: mfaStatus } = useGetUserMfaStatus(); // Orval generated hook for fetching MFA status

  const { control, register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      mfaEnabled: false,
      password: "",
      email: "",
      code: "",
      message: {
        msg: "",
        color: "green",
      },
      state: "", // confirmSignUp
    },
  });

  useEffect(() => { // set form values based on fetched MFA status
    if (mfaStatus) {
      setValue("mfaEnabled", mfaStatus.mfaEnabled ?? false);
      setValue("email", mfaStatus.email ?? "");
      setValue("state", mfaStatus.mfaEnabled ? "mfaEnabled" : "mfaDisabled");
    }
  }, [mfaStatus, setValue]);

  // Disable MFA // TODO: also verify MFA code when disabling MFA for better security
  const onSubmit = async (data) => {
    try {
      const status = await upsertMfaSettings(data);
      setValue("mfaEnabled", status.mfaEnabled);
      setValue("state", "mfaDisabled");
      setValue("message", { msg: t("MFAdisabled", "MFA on poistettu käytöstä"), color: "green" });
    } catch {
      setValue("message", { msg: t("unauthorized", "Virheellinen käyttäjätunnus tai salasana"), color: "red" });
    }
  };

  const message = watch("message");

  return (
    <Modal show onHide={hideModal} fullscreen="lg-down" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t("SettingsLogin", "Kirjautumisasetukset")}</Modal.Title>
      </Modal.Header>

      <Modal.Body><form onSubmit={handleSubmit(onSubmit)}>
        <Switch
          control={control}
          id="mfaEnabled"
          name="mfaEnabled"
          label={t("MFA", "Monivaiheinen tunnistautuminen")}
        />

        <Form.Group>
          <Form.Label>{t("Username", "Käyttäjätunnus")}</Form.Label>
          <Form.Control disabled value={user_name} />
        </Form.Group>

        <Form.Group controlId="password">
          <Form.Label>{t("Password", "Salasana")}</Form.Label>
          <Form.Control
            type="password"
            {...register("password", { required: true })}
          />
        </Form.Group>

        <Form.Group controlId="email">
          <Form.Label>{t("Email", "Sähköposti")}{errors.email && <span style={{color: "red"}}> *</span>}</Form.Label>
          <Form.Control
            type="email"
            {...register("email", { required: true })}
          />
        </Form.Group>

        {watch("state") === "verifyCode" && (
          <Row>
            <Col>
              <Form.Group controlId="code">
                <Form.Label>{t("ConfirmationCodeFromEmail", "Vahvistuskoodi sähköpostista")}</Form.Label>
                <Form.Control {...register("code", { required: true })} autoFocus />
              </Form.Group>
            </Col>
          </Row>
        )}

        <p style={{ color: message.color }}>{message.msg}</p>

        {(watch("state") === "mfaDisabled" && watch("mfaEnabled") === true && watch("password") && watch("email")) && (
          <Button variant="primary" style={{ marginLeft: "auto" }} onClick={async () => {
            const result = await verifyMFAeMail({
              email: watch("email"),
              password: watch("password"),
            });
            if (result === true) {
              setValue("message", {});
              setValue("state", "verifyCode");
            } else {
              setValue("message", { msg: t("unauthorized", "Virheellinen käyttäjätunnus tai salasana"), color: "red" });
            }
          }}>
            {t("VerifyEmail", "Vahvista sähköposti")}
          </Button>
        )}

        {(watch("code") && watch("mfaEnabled") === true && watch("password") && watch("email")) && (
          <Button variant="primary" style={{ marginLeft: "auto" }} onClick={async () => {
            const result = await verifyCodeAndEnableMFA({
              email: watch("email"),
              password: watch("password"),
              code: watch("code").trim(),
            });
            if (result === true) {
              setValue("code", "");
              setValue("state", "mfaEnabled");
              setValue("message", { msg: t("MFAenabled", "MFA on nyt käytössä"), color: "green" });
            } else {
              setValue("message", { msg: t("MFAenableFailed", "MFA:n käyttöönotto epäonnistui"), color: "red" });
            }
          }}>
          {t("MFAenable", "Ota MFA käyttöön")}
          </Button>
        )}

        {(watch("state") === "mfaEnabled" && watch("mfaEnabled") === false) && (
          <Button variant="primary" type="submit" style={{ marginLeft: "auto" }}>
            {t("MFAdisable", "Poista MFA käytöstä")}
          </Button>
        )}

        </form></Modal.Body>
    </Modal>
  );
}
